/**
 * "What do you need today?" card quiz.
 * Pop-up dialog that asks five whimsical questions and recommends cards from the shop's own
 * shop's own products (via a small JSON view of the catalogue, templates/collection.quiz-data.liquid). Cards are
 * picked by their Occasion metafield and scored on Recipient, Personality and Style. A card can carry several values.
 *
 * Open it from anywhere with: a button/link carrying data-open-card-quiz, a link to #card-quiz,
 * or window.openCardQuiz().
 */
(() => {
  const root = document.querySelector('[data-card-quiz]');
  if (!root) return;

  const dialog = root.querySelector('dialog');
  const body = root.querySelector('[data-cq-body]');
  const launcher = document.querySelector('[data-cq-launcher]');
  if (!dialog || !body) return;

  const SEEN_KEY = 'ygpoyf-card-quiz-seen';
  const template = root.dataset.template || '';
  const autoOpenSeconds = parseInt(root.dataset.autoOpen || '0', 10);

  // ---- Content -------------------------------------------------------------------------------

  const QUESTIONS = [
    {
      id: 'occasion',
      title: "What's the occasion?",
      hint: "Go on, tell us what we're celebrating.",
      options: ['Birthday', 'Love & friendship', 'Thank you', 'New baby', 'Just because', 'Something else'],
    },
    {
      id: 'recipient',
      title: "Who's the lucky recipient?",
      hint: "Who's about to get the good stuff?",
      options: ['Partner', 'Friend', 'Mum or Dad', 'Brother or Sister', 'Child', 'Colleague', 'Someone else'],
    },
    {
      id: 'personality',
      title: 'What are they like?',
      hint: "Be honest. We won't tell them.",
      options: [
        'A bit of a joker',
        'Sentimental (but won’t admit it)',
        'Retro obsessed',
        'Food is their personality',
        'Into the weird and wonderful',
        'Impossible to describe',
      ],
    },
    {
      id: 'style',
      title: 'What sort of card are you after?',
      hint: 'Pick a vibe.',
      options: ['Funny', 'Sweet, not soppy', 'Bold and colourful', 'Small but mighty', 'Surprise me'],
    },
    {
      id: 'quantity',
      title: 'Just one card, or stocking up?',
      hint: 'No judgement if you’re hoarding.',
      options: ['Just the one', 'A few for upcoming occasions', 'Show me some minis too'],
    },
  ];

  // Shown after "Something else": narrows the occasion down.
  const SUB_QUESTION = {
    id: 'occasion2',
    title: 'Which occasion, exactly?',
    hint: 'Pick the closest one.',
    options: [
      'Wedding',
      'Good luck',
      'Well done',
      'Engagement',
      'New home',
      'New job',
      'Leaving',
      'Sympathy',
      'Get well soon',
      'Driving test',
      'Pregnancy',
      'Something different',
    ],
  };

  const OCCASION = {
    Birthday: {
      collections: ['hand-drawn-birthday-cards', 'retro-birthday-cards', 'kids-birthday-cards', 'male-cards', 'female-cards'],
      occasions: ['birthday'],
      tags: ['birthday', 'birthday card'],
      url: '/collections/hand-drawn-birthday-cards',
      noun: 'birthday',
    },
    'Love & friendship': {
      collections: ['love-cards', 'anniversary-cards'],
      occasions: ['love and friendship', 'anniversary'],
      tags: ['love', 'friendship', 'anniversary'],
      url: '/collections/love-cards',
      noun: 'love and friendship',
    },
    'Thank you': {
      collections: ['thank-you-cards'],
      occasions: ['thank you'],
      tags: ['thank you'],
      url: '/collections/thank-you-cards',
      noun: 'thank you',
    },
    'New baby': {
      collections: ['new-baby-cards', 'pregnant-mama'],
      occasions: ['new baby'],
      tags: ['new baby', 'twin baby', 'pregnant mama'],
      url: '/collections/new-baby-cards',
      noun: 'new baby',
    },
    'Just because': {
      collections: ['just-because-cards'],
      occasions: ['just because'],
      tags: ['just because', 'everyday greeting card'],
      url: '/collections/just-because-cards',
      noun: 'just because',
    },
    'Something else': {
      collections: [
        'other-occasions-cards',
        'well-done-cards',
        'good-luck-cards',
        'sympathy-cards',
        'get-well-soon-cards',
        'new-home',
        'new-job',
        'wedding-cards',
        'engagement-cards',
        'leaving-cards',
        'driving-test-cards',
        'anniversary-cards',
      ],
      occasions: ['congratulations', 'good luck', 'thinking of you', 'wedding', 'anniversary', "valentine's day"],
      tags: [
        'congratulations',
        'good luck',
        'well done',
        'sympathy',
        'get well',
        'thinking of you',
        'new home',
        'new job',
        'wedding',
        'engagement',
        'leaving',
        'driving test',
        'exam pass',
        'good job',
        'safe travel',
        'other occasions',
      ],
      url: '/collections/other-occasions-cards',
      noun: 'special-occasion',
    },
  };

  // Second-page choices under "Something else". Cards match on their Occasion metafield (lower case values).
  const sub = (occasions, collections, url, noun) => ({ occasions, collections, tags: [], url, noun });
  const SUB_OCCASION = {
    Wedding: sub(['wedding'], ['wedding-cards'], '/collections/wedding-cards', 'wedding'),
    'Good luck': sub(['good luck'], ['good-luck-cards'], '/collections/good-luck-cards', 'good luck'),
    'Well done': sub(['well done', 'congratulations'], ['well-done-cards'], '/collections/well-done-cards', 'well done'),
    Engagement: sub(['engagement'], ['engagement-cards'], '/collections/engagement-cards', 'engagement'),
    'New home': sub(['new home'], ['new-home'], '/collections/new-home', 'new home'),
    'New job': sub(['new job'], ['new-job'], '/collections/new-job', 'new job'),
    Leaving: sub(['leaving'], ['leaving-cards'], '/collections/leaving-cards', 'leaving'),
    Sympathy: sub(['sympathy'], ['sympathy-cards'], '/collections/sympathy-cards', 'sympathy'),
    'Get well soon': sub(['get well soon'], ['get-well-soon-cards'], '/collections/get-well-soon-cards', 'get well soon'),
    'Driving test': sub(['driving test'], ['driving-test-cards'], '/collections/driving-test-cards', 'driving test'),
    Pregnancy: sub(['pregnancy'], ['pregnant-mama'], '/collections/pregnant-mama', 'pregnancy'),
    'Something different': sub(
      ['thinking of you', 'congratulations', 'good luck', "valentine's day"],
      ['other-occasions-cards'],
      '/collections/other-occasions-cards',
      'special-occasion'
    ),
  };

  const MINI_COLLECTIONS = ['icon-mini-greeting-cards', 'matchbox-minis-cards'];
  const MINI_TAGS = ['minis', 'matchbox minis'];
  const FOOD_WORDS = /\b(pizza|cheese|toast|chips|fries|cake|cupcake|tea|coffee|biscuit|croissant|burger|sushi|egg|bacon|banana|avocado|cherry|chilli|chip|sandwich|marmite|pie|crisps|doughnut|donut|jam|butter|beans)\b/i;

  // What each answer looks for on a card. Values are lower case versions of the Recipient, Personality and Style fields.
  const RECIPIENT_VALUES = {
    Partner: ['partner'],
    Friend: ['friend'],
    'Mum or Dad': ['mum', 'dad'],
    'Brother or Sister': ['brother', 'sister'],
    Child: ['child'],
    Colleague: ['colleague'],
  };
  // Recipients with no card field of their own also score on the occasion.
  const RECIPIENT_OCCASIONS = {
    Partner: ['love and friendship', 'anniversary'],
    Colleague: ['leaving', 'well done', 'new job', 'congratulations'],
  };
  const PERSONALITY_VALUES = {
    'A bit of a joker': 'a bit of a joker',
    'Sentimental (but won’t admit it)': 'sentimental',
    'Retro obsessed': 'retro obsessed',
    'Food is their personality': 'food is their personality',
    'Into the weird and wonderful': 'weird and wonderful',
  };
  const STYLE_VALUES = {
    Funny: 'funny',
    'Sweet, not soppy': 'sweet, not soppy',
    'Bold and colourful': 'bold and colourful',
    'Small but mighty': 'small but mighty',
  };

  const HEADLINES = {
    'A bit of a joker': ['Right, these have jokes.', 'Pens down: a few belters for your comedian.'],
    'Sentimental (but won’t admit it)': ['Soft centres, found.', 'Tissues optional. Here are your cards.'],
    'Retro obsessed': ['Press play on these.', 'Straight out of a very good decade.'],
    'Food is their personality': ['Snack-sized cards, coming up.', 'Dinner is served (in card form).'],
    'Into the weird and wonderful': ['Oh, they’re going to love these.', 'Delightfully strange, just like them.'],
    'Impossible to describe': ['We took a leap. Ta-da!', 'No idea who they are, but these are lovely.'],
  };

  // ---- Helpers -------------------------------------------------------------------------------

  const esc = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const poolCache = new Map();

  const fetchCollection = async (handle) => {
    if (poolCache.has(handle)) return poolCache.get(handle);
    const request = fetch(`/collections/${handle}?view=quiz-data`)
      .then((response) => (response.ok ? response.text() : '{"products":[]}'))
      .then((text) => JSON.parse(text).products || [])
      .catch(() => []);
    poolCache.set(handle, request);
    return request;
  };

  // Gift bags, wrap, prints and stickers can sit in card collections; the quiz only recommends cards.
  const NOT_CARDS = ['gift bag', 'gift wrap', 'flat wrap', 'art prints', 'print', 'sticker', 'sticker sheet'];
  const isCard = (product) => {
    const type = String(product.type || '').toLowerCase();
    const tags = (product.tags || []).map((tag) => String(tag).toLowerCase());
    return !NOT_CARDS.includes(type) && !tags.some((tag) => NOT_CARDS.includes(tag));
  };

  const fetchPool = async (handles) => {
    const lists = await Promise.all(handles.map(fetchCollection));
    const byId = new Map();
    lists.flat().forEach((product) => byId.set(product.id, product));
    return [...byId.values()].filter((p) => p.available !== false && isCard(p));
  };

  // Every product in the shop, 250 per page.
  let allProducts;
  const fetchAllProducts = () => {
    if (!allProducts) {
      allProducts = (async () => {
        const all = [];
        for (let page = 1; page <= 8; page += 1) {
          const batch = await fetch(`/collections/all?view=quiz-data&page=${page}`)
            .then((response) => (response.ok ? response.text() : '{"products":[]}'))
            .then((text) => JSON.parse(text).products || [])
            .catch(() => []);
          all.push(...batch);
          if (batch.length < 250) break;
        }
        return all;
      })();
    }
    return allProducts;
  };

  // A card belongs to an occasion when its Occasion metafield says so.
  const matchesOccasion = (product, occasion) =>
    (product.occasion || []).some((value) => occasion.occasions.includes(String(value).toLowerCase()));

  // Cards with an Occasion are picked by it. Cards that don't have one yet fall back to the occasion's collections.
  const occasionPool = async (occasion) => {
    const [all, legacy] = await Promise.all([fetchAllProducts(), fetchPool(occasion.collections)]);
    const byId = new Map();
    all.filter((p) => p.available !== false && isCard(p) && matchesOccasion(p, occasion)).forEach((p) => byId.set(p.id, p));
    legacy.filter((p) => !(p.occasion || []).length).forEach((p) => byId.set(p.id, p));
    return [...byId.values()];
  };

  const lower = (list) => new Set((list || []).map((value) => String(value).toLowerCase()));
  const tagSet = (product) => lower(product.tags);
  const hasAny = (set, wanted) => wanted.some((value) => set.has(value));

  const scoreProduct = (product, answers) => {
    const recipient = lower(product.recipient);
    const personality = lower(product.personality);
    const style = lower(product.style);
    const occasions = lower(product.occasion);
    let score = Math.random() * 1.5;

    const recipientValues = RECIPIENT_VALUES[answers.recipient];
    if (recipientValues && hasAny(recipient, recipientValues)) score += answers.recipient === 'Child' ? 5 : 4;
    const recipientOccasions = RECIPIENT_OCCASIONS[answers.recipient];
    if (recipientOccasions && hasAny(occasions, recipientOccasions)) score += 2;

    const wantedPersonality = PERSONALITY_VALUES[answers.personality];
    if (wantedPersonality && personality.has(wantedPersonality)) score += 4;
    if (answers.personality === 'Food is their personality' && FOOD_WORDS.test(product.title)) score += 3;

    const wantedStyle = STYLE_VALUES[answers.style];
    if (wantedStyle && style.has(wantedStyle)) score += wantedStyle === 'small but mighty' ? 6 : 3;
    if (answers.style === 'Small but mighty' && hasAny(tagSet(product), MINI_TAGS)) score += 6;
    if (answers.style === 'Surprise me') score += Math.random() * 4;
    return score;
  };

  const rank = (products, answers) =>
    products
      .map((product) => ({ product, score: scoreProduct(product, answers) }))
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.product);

  const productTile = (product, featured) => `
    <a class="cq-product${featured ? ' cq-product--featured' : ''}" href="/products/${esc(product.handle)}">
      <span class="cq-product__media">${
        product.image ? `<img src="${esc(product.image)}" alt="" loading="lazy" width="480" height="480">` : ''
      }</span>
      <span class="cq-product__title">${esc(product.title)}</span>
      <span class="cq-product__price">${esc(product.price || '')}</span>
    </a>`;

  const pick = (list) => list[Math.floor(Math.random() * list.length)];

  // ---- State & rendering ---------------------------------------------------------------------

  let step = -1; // -1 = intro, 0..n-1 = questions, n = results
  let answers = {};

  // "Something else" adds a second page of occasions after question 1.
  const getQuestions = () =>
    answers.occasion === 'Something else' ? [QUESTIONS[0], SUB_QUESTION, ...QUESTIONS.slice(1)] : QUESTIONS;

  const currentOccasion = () =>
    answers.occasion === 'Something else' && SUB_OCCASION[answers.occasion2]
      ? SUB_OCCASION[answers.occasion2]
      : OCCASION[answers.occasion] || OCCASION['Something else'];

  const progress = () =>
    `<div class="cq-progress" aria-hidden="true">${getQuestions().map(
      (_, i) => `<span class="${i <= step ? 'is-done' : ''}"></span>`
    ).join('')}</div>`;

  const scribble =
    '<svg class="cq-scribble" viewBox="0 0 220 12" aria-hidden="true" focusable="false"><path d="M2 8 C 30 2, 60 11, 95 6 S 160 3, 218 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';

  const renderIntro = () => {
    body.innerHTML = `
      <div class="cq-intro">
        <p class="cq-eyebrow">Find the perfect card&hellip;</p>
        <h2 class="cq-title" id="cq-title">What do you need today?</h2>
        ${scribble}
        <p class="cq-lede">Five quick questions, zero judgement. We'll rummage through the card drawers for you.</p>
        <button type="button" class="cq-button" data-cq-start>Let’s go</button>
      </div>`;
    body.querySelector('[data-cq-start]').focus();
  };

  const renderQuestion = () => {
    const question = getQuestions()[step];
    const options = question.options
      .map(
        (label, i) =>
          `<button type="button" class="cq-option${answers[question.id] === label ? ' is-selected' : ''}" data-cq-option="${esc(
            label
          )}" style="--tilt:${i % 2 ? 0.5 : -0.5}deg;--i:${i}">${esc(label)}</button>`
      )
      .join('');
    body.innerHTML = `
      ${progress()}
      <p class="cq-eyebrow">Question ${step + 1} of ${getQuestions().length}</p>
      <h2 class="cq-title" id="cq-title">${esc(question.title)}</h2>
      <p class="cq-hint">${esc(question.hint)}</p>
      <div class="cq-options" role="group" aria-labelledby="cq-title">${options}</div>
      <div class="cq-nav"><button type="button" class="cq-link" data-cq-back>← Back</button></div>`;
    const first = body.querySelector('.cq-option');
    if (first) first.focus({ preventScroll: true });
  };

  const renderLoading = () => {
    body.innerHTML = `
      <div class="cq-loading" role="status">
        <p class="cq-eyebrow">One moment&hellip;</p>
        <h2 class="cq-title" id="cq-title">Rummaging through the card drawers</h2>
        <div class="cq-dots" aria-hidden="true"><span></span><span></span><span></span></div>
      </div>`;
  };

  const fallbackResult = () => {
    const info = currentOccasion();
    body.innerHTML = `
      <div class="cq-results">
        <p class="cq-eyebrow">Hmm.</p>
        <h2 class="cq-title" id="cq-title">The card drawers are being stubborn</h2>
        <p class="cq-lede">Have a rummage yourself, we've pointed you to the right shelf.</p>
        <div class="cq-actions">
          <a class="cq-button" href="${esc(info.url)}">Browse ${esc(info.noun)} cards</a>
          <button type="button" class="cq-link" data-cq-restart>Start again</button>
        </div>
      </div>`;
  };

  const buildResults = async () => {
    const occasion = currentOccasion();
    const pool = await occasionPool(occasion);
    if (!pool.length) return null;

    const ranked = rank(pool, answers);
    const stockingUp = answers.quantity === 'A few for upcoming occasions';
    const wantsMinis = answers.quantity === 'Show me some minis too' || answers.style === 'Small but mighty';

    let picks;
    let extraTitle = '';
    let extras = [];

    if (stockingUp) {
      const used = new Set();
      picks = [];
      ranked.slice(0, 2).forEach((p) => {
        picks.push(p);
        used.add(p.id);
      });
      const others = ['Birthday', 'Thank you', 'Just because', 'Love & friendship'].filter((o) => o !== answers.occasion);
      for (const name of others) {
        if (picks.length >= 4) break;
        const otherPool = await occasionPool(OCCASION[name]);
        const best = rank(otherPool, { ...answers, recipient: '' }).find((p) => !used.has(p.id));
        if (best) {
          picks.push(best);
          used.add(best.id);
        }
      }
    } else {
      picks = ranked.slice(0, 3);
    }

    if (wantsMinis) {
      const minis = await fetchPool(MINI_COLLECTIONS);
      const matching = minis.filter((p) => ((p.occasion || []).length ? matchesOccasion(p, occasion) : hasAny(tagSet(p), occasion.tags || [])));
      const chosen = rank(matching.length >= 3 ? matching : minis, answers).filter((p) => !picks.some((x) => x.id === p.id));
      extras = chosen.slice(0, 3);
      extraTitle = 'And a few minis, because why not';
    }

    return { picks, extras, extraTitle, occasion };
  };

  const renderResults = (result) => {
    const { picks, extras, extraTitle, occasion } = result;
    const stockingUp = answers.quantity === 'A few for upcoming occasions';
    const headline = pick(HEADLINES[answers.personality] || HEADLINES['Impossible to describe']);
    const recipient = answers.recipient && answers.recipient !== 'Someone else' ? answers.recipient.toLowerCase() : 'your someone';
    const sub = stockingUp
      ? 'A little stash for the drawer: the best match first, then a few for rainy-day occasions.'
      : `Our pick for ${recipient} first, then a couple of runners-up.`;
    const tiles = picks.map((p, i) => productTile(p, !stockingUp && i === 0)).join('');
    const extrasHtml = extras.length
      ? `<h3 class="cq-subtitle">${esc(extraTitle)}</h3><div class="cq-grid cq-grid--small">${extras
          .map((p) => productTile(p, false))
          .join('')}</div>`
      : '';
    body.innerHTML = `
      <div class="cq-results">
        <p class="cq-eyebrow">Found them</p>
        <h2 class="cq-title" id="cq-title">${esc(headline)}</h2>
        ${scribble}
        <p class="cq-lede">${esc(sub)}</p>
        <div class="cq-grid${stockingUp ? ' cq-grid--stock' : ''}">${tiles}</div>
        ${extrasHtml}
        <div class="cq-actions">
          <a class="cq-button" href="${esc(occasion.url)}">See all ${esc(occasion.noun)} cards</a>
          <button type="button" class="cq-link" data-cq-restart>Start again</button>
        </div>
      </div>`;
    const heading = body.querySelector('#cq-title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    dialog.scrollTo({ top: 0 });
  };

  const showResults = async () => {
    step = getQuestions().length;
    renderLoading();
    document.dispatchEvent(new CustomEvent('card-quiz:complete', { detail: { ...answers } }));
    try {
      const result = await buildResults();
      if (step !== getQuestions().length) return; // closed or restarted while loading
      if (result && result.picks.length) renderResults(result);
      else fallbackResult();
    } catch (e) {
      fallbackResult();
    }
  };

  const goTo = (next) => {
    step = next;
    if (step < 0) renderIntro();
    else if (step < getQuestions().length) renderQuestion();
    else showResults();
  };

  // ---- Events --------------------------------------------------------------------------------

  body.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-cq-start]')) return goTo(0);
    if (target.closest('[data-cq-restart]')) {
      answers = {};
      return goTo(-1);
    }
    if (target.closest('[data-cq-back]')) return goTo(step - 1);

    const option = target.closest('[data-cq-option]');
    if (option) {
      const question = getQuestions()[step];
      answers[question.id] = option.dataset.cqOption;
      if (question.id === 'occasion') delete answers.occasion2;
      body.querySelectorAll('.cq-option').forEach((el) => el.classList.toggle('is-selected', el === option));
      window.setTimeout(() => goTo(step + 1), 240);
    }
  });

  const open = () => {
    if (dialog.open) return;
    // Tuck the mobile menu away first if the quiz was opened from it.
    const drawer = document.querySelector('header-drawer');
    if (drawer && drawer.isOpen && typeof drawer.close === 'function') drawer.close();
    answers = {};
    goTo(-1);
    dialog.showModal();
    document.documentElement.classList.add('cq-open');
    try {
      localStorage.setItem(SEEN_KEY, String(Date.now()));
    } catch (e) {}
  };

  const close = () => {
    if (dialog.open) dialog.close();
  };

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('cq-open');
    step = -1;
  });

  root.querySelector('[data-cq-close]')?.addEventListener('click', close);
  // Click on the backdrop (the dialog element itself) closes it.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest('[data-open-card-quiz], a[href="#card-quiz"], a[href$="#card-quiz"]');
    if (trigger) {
      event.preventDefault();
      open();
    }
  });

  window.openCardQuiz = open;

  // ---- Auto open (once every 14 days) --------------------------

  // If a collection handle is set in the theme settings (default: the main Cards collection) the pop-up only
  // appears on that collection page. Leave it blank to allow the homepage and every collection page.
  const autoOpenCollection = (root.dataset.autoOpenCollection || '').trim();
  const autoOpenAllowed = autoOpenCollection
    ? template === 'collection' && root.dataset.collectionHandle === autoOpenCollection
    : ['index', 'collection'].includes(template);
  if (autoOpenSeconds > 0 && autoOpenAllowed) {
    let seenRecently = false;
    try {
      const seen = parseInt(localStorage.getItem(SEEN_KEY) || '0', 10);
      seenRecently = seen && Date.now() - seen < 14 * 24 * 60 * 60 * 1000;
    } catch (e) {}

    if (!seenRecently) {
      window.setTimeout(() => {
        const busy = document.querySelector('dialog[open], .cart-drawer[open], [aria-modal="true"]:not([hidden])');
        if (!busy && !dialog.open) open();
      }, autoOpenSeconds * 1000);
    }
  }

  if (launcher) launcher.hidden = false;
})();
